import React, { useState, useEffect } from "react";
import { db } from "./firebase";
import {
  collection, addDoc, getDocs, deleteDoc, doc, updateDoc,
  query, where, serverTimestamp, getDoc,
} from "firebase/firestore";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import ImageCropModal from "./components/ImageCropModal";
import LatexRenderer from "./LatexRenderer";

const CLASSES = ["PUC-I", "PUC-II", "Engineering Physics"];
const BATCHES = ["C1", "C2", "Both"];

// Helper: determine quiz status based on openFrom/openUntil
function getQuizStatus(quiz) {
  const now = Date.now();
  const from = quiz.openFrom ? new Date(quiz.openFrom).getTime() : null;
  const until = quiz.openUntil ? new Date(quiz.openUntil).getTime() : null;
  if (!from && !until) return { label: "🔓 Always Open", color: "#1976d2", state: "always" };
  if (from && now < from) return { label: "⏳ Upcoming", color: "#f57c00", state: "upcoming" };
  if (until && now > until) return { label: "⚪ Closed", color: "#9e9e9e", state: "closed" };
  return { label: "🟢 Live", color: "#43a047", state: "live" };
}

function TeacherDashboard({ user, assignedClasses = [] }) {
  const [activeTab, setActiveTab] = useState("myClasses");
  const [myQuizzes, setMyQuizzes] = useState([]);
  const [allResults, setAllResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [editingQuiz, setEditingQuiz] = useState(null);
  const [launchingQuiz, setLaunchingQuiz] = useState(null); // quiz being launched (shows modal)
  const [launchDuration, setLaunchDuration] = useState(60); // minutes

  const [quizForm, setQuizForm] = useState({
    class: assignedClasses[0] || "",
    title: "",
    timeLimit: 30,
    assignedBatch: "Both",
    openFrom: "",
    openUntil: "",
    questions: [],
    newQuestion: { type: "mcq", question: "", options: ["", "", "", ""], answer: 0, imageUrl: "", imageMode: "none" },
  });

  useEffect(() => {
    if (user?.email) {
      fetchMyQuizzes();
    }
  }, [user?.email]);

  const fetchMyQuizzes = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, "quizzes"),
        where("createdBy", "==", user.email)
      );
      const querySnapshot = await getDocs(q);
      const quizzes = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setMyQuizzes(quizzes);

      // Also fetch results for these quizzes - filter results where the quiz was created by this teacher
      const allResults = [];
      quizzes.forEach(quiz => {
        // Get results for each quiz created by this teacher
        getDocs(query(
          collection(db, "quizResults"),
          where("quizId", "==", quiz.id)
        )).then(resultsSnapshot => {
          allResults.push(...resultsSnapshot.docs.map(doc => ({ ...doc.data() })));
          setAllResults([...allResults]);
        });
      });
    } catch (error) {
      console.error("Error fetching quizzes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddQuestion = () => {
    setQuizForm(prev => ({
      ...prev,
      questions: [...prev.questions, { ...prev.newQuestion }],
      newQuestion: { type: "mcq", question: "", options: ["", "", "", ""], answer: 0, imageUrl: "", imageMode: "none" }
    }));
  };

  const handleRemoveQuestion = (index) => {
    setQuizForm(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
  };

  const handleUpdateQuestion = (index, field, value) => {
    setQuizForm(prev => {
      const updated = [...prev.questions];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, questions: updated };
    });
  };

  const handleSaveQuiz = async () => {
    if (!quizForm.class || !quizForm.title || quizForm.questions.length === 0) {
      alert("Please fill in all required fields and add at least one question.");
      return;
    }

    try {
      setLoading(true);
      const quizData = {
        class: quizForm.class,
        title: quizForm.title,
        timeLimit: parseInt(quizForm.timeLimit),
        assignedBatch: quizForm.assignedBatch,
        questions: quizForm.questions,
        openFrom: quizForm.openFrom || null,
        openUntil: quizForm.openUntil || null,
        createdBy: user.email,
        createdByName: user.displayName,
        createdByRole: "teacher",
        createdAt: editingQuiz ? editingQuiz.createdAt : serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      if (editingQuiz) {
        await updateDoc(doc(db, "quizzes", editingQuiz.id), quizData);
        alert("Quiz updated successfully!");
      } else {
        await addDoc(collection(db, "quizzes"), quizData);
        alert("Quiz created successfully!");
      }

      resetQuizForm();
      fetchMyQuizzes();
    } catch (error) {
      console.error("Error saving quiz:", error);
      alert("Error saving quiz: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const resetQuizForm = () => {
    setQuizForm({
      class: assignedClasses[0] || "",
      title: "",
      timeLimit: 30,
      assignedBatch: "Both",
      openFrom: "",
      openUntil: "",
      questions: [],
      newQuestion: { type: "mcq", question: "", options: ["", "", "", ""], answer: 0, imageUrl: "", imageMode: "none" },
    });
    setEditingQuiz(null);
  };

  // ── Launch Quiz: opens it now, closes after specified minutes ──
  const handleLaunchQuiz = async () => {
    if (!launchingQuiz) return;
    const minutes = parseInt(launchDuration);
    if (!minutes || minutes <= 0) {
      alert("Please enter a valid duration in minutes.");
      return;
    }
    try {
      setLoading(true);
      const now = new Date();
      const until = new Date(now.getTime() + minutes * 60 * 1000);
      await updateDoc(doc(db, "quizzes", launchingQuiz.id), {
        openFrom: now.toISOString(),
        openUntil: until.toISOString(),
        updatedAt: serverTimestamp(),
      });
      alert(`✅ Quiz launched! Open until ${until.toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}`);
      setLaunchingQuiz(null);
      setLaunchDuration(60);
      fetchMyQuizzes();
    } catch (error) {
      console.error("Error launching quiz:", error);
      alert("Error launching quiz: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Close Quiz: ends it immediately ──
  const handleCloseQuiz = async (quiz) => {
    if (!window.confirm(`Close "${quiz.title}" now? Students won't be able to take it after closing.`)) return;
    try {
      setLoading(true);
      await updateDoc(doc(db, "quizzes", quiz.id), {
        openUntil: new Date().toISOString(),
        updatedAt: serverTimestamp(),
      });
      alert("✅ Quiz closed!");
      fetchMyQuizzes();
    } catch (error) {
      console.error("Error closing quiz:", error);
      alert("Error closing quiz: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuiz = async (quizId) => {
    if (!window.confirm("Are you sure you want to delete this quiz?")) return;
    try {
      await deleteDoc(doc(db, "quizzes", quizId));
      alert("Quiz deleted successfully!");
      fetchMyQuizzes();
    } catch (error) {
      console.error("Error deleting quiz:", error);
      alert("Error deleting quiz: " + error.message);
    }
  };

  const handleEditQuiz = (quiz) => {
    setEditingQuiz(quiz);
    setQuizForm({
      class: quiz.class,
      title: quiz.title,
      timeLimit: quiz.timeLimit,
      assignedBatch: quiz.assignedBatch,
      openFrom: quiz.openFrom || "",
      openUntil: quiz.openUntil || "",
      questions: quiz.questions,
      newQuestion: { type: "mcq", question: "", options: ["", "", "", ""], answer: 0, imageUrl: "", imageMode: "none" },
    });
    setActiveTab("createQuiz");
  };

  const handleExportResults = async () => {
    try {
      setLoading(true);
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Results");

      // Headers
      worksheet.columns = [
        { header: "Quiz Title", key: "quizTitle", width: 25 },
        { header: "Student Name", key: "studentName", width: 20 },
        { header: "Student Email", key: "studentEmail", width: 25 },
        { header: "Class", key: "class", width: 15 },
        { header: "Batch", key: "studentBatch", width: 10 },
        { header: "Score", key: "score", width: 8 },
        { header: "Total", key: "total", width: 8 },
        { header: "Percentage", key: "percent", width: 12 },
        { header: "Grade", key: "grade", width: 10 },
        { header: "Submitted At", key: "submittedAt", width: 20 },
      ];

      // Add data
      allResults.forEach(result => {
        const grade = result.percent >= 90 ? "A+" : result.percent >= 75 ? "A" : result.percent >= 50 ? "B" : "C";
        worksheet.addRow({
          quizTitle: result.quizTitle,
          studentName: result.studentName,
          studentEmail: result.studentEmail,
          class: result.class,
          studentBatch: result.studentBatch,
          score: result.score,
          total: result.total,
          percent: result.percent,
          grade: grade,
          submittedAt: result.submittedAt,
        });
      });

      // Save file
      await workbook.xlsx.writeBuffer().then(buffer => {
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        saveAs(blob, `quiz-results-${new Date().toISOString().split("T")[0]}.xlsx`);
      });

      alert("Results exported successfully!");
    } catch (error) {
      console.error("Error exporting results:", error);
      alert("Error exporting results: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="teacher-dashboard" style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
      <h1 style={{ marginBottom: "20px" }}>🎓 Teacher Dashboard</h1>

      {/* Tabs */}
      <div style={{ display: "flex", gap: "10px", borderBottom: "2px solid #ccc", marginBottom: "20px" }}>
        {[
          { id: "myClasses", label: "My Classes" },
          { id: "createQuiz", label: "Create Quiz" },
          { id: "myQuizzes", label: "My Quizzes" },
          { id: "results", label: "Quiz Results" },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => { setActiveTab(tab.id); if (tab.id === "createQuiz") resetQuizForm(); }}
            style={{
              padding: "10px 20px",
              border: "none",
              background: activeTab === tab.id ? "#1565c0" : "#f5f5f5",
              color: activeTab === tab.id ? "#fff" : "#000",
              cursor: "pointer",
              borderRadius: "4px 4px 0 0",
              fontWeight: activeTab === tab.id ? "bold" : "normal",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* My Classes Tab */}
      {activeTab === "myClasses" && (
        <div>
          <h2>My Assigned Classes</h2>
          {assignedClasses.length === 0 ? (
            <p style={{ color: "#999" }}>No classes assigned. Contact admin to get assigned to classes.</p>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "15px" }}>
              {assignedClasses.map(cls => (
                <div
                  key={cls}
                  style={{
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    padding: "15px",
                    textAlign: "center",
                    background: "#f9f9f9",
                  }}
                >
                  <h3>{cls}</h3>
                  <p style={{ color: "#666" }}>
                    {myQuizzes.filter(q => q.class === cls).length} quiz(zes) created
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create Quiz Tab */}
      {activeTab === "createQuiz" && (
        <div>
          <h2>{editingQuiz ? "Edit Quiz" : "Create New Quiz"}</h2>
          <div style={{ background: "#f5f5f5", padding: "15px", borderRadius: "8px", marginBottom: "15px" }}>
            <div style={{ marginBottom: "15px" }}>
              <label>Class: </label>
              <select
                value={quizForm.class}
                onChange={e => setQuizForm(prev => ({ ...prev, class: e.target.value }))}
                disabled={assignedClasses.length === 0}
                style={{ padding: "8px", marginLeft: "10px" }}
              >
                <option value="">Select Class</option>
                {assignedClasses.map(cls => (
                  <option key={cls} value={cls}>{cls}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: "15px" }}>
              <label>Quiz Title: </label>
              <input
                type="text"
                value={quizForm.title}
                onChange={e => setQuizForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter quiz title"
                style={{ padding: "8px", marginLeft: "10px", width: "300px" }}
              />
            </div>

            <div style={{ marginBottom: "15px" }}>
              <label>Time Limit (minutes): </label>
              <input
                type="number"
                value={quizForm.timeLimit}
                onChange={e => setQuizForm(prev => ({ ...prev, timeLimit: e.target.value }))}
                style={{ padding: "8px", marginLeft: "10px", width: "100px" }}
              />
            </div>

            <div style={{ marginBottom: "15px" }}>
              <label>Assigned Batch: </label>
              <select
                value={quizForm.assignedBatch}
                onChange={e => setQuizForm(prev => ({ ...prev, assignedBatch: e.target.value }))}
                style={{ padding: "8px", marginLeft: "10px" }}
              >
                {BATCHES.map(batch => (
                  <option key={batch} value={batch}>{batch}</option>
                ))}
              </select>
            </div>

            {/* Schedule */}
            <div style={{ background: "#e3f2fd", padding: "12px", borderRadius: "6px", marginTop: "10px" }}>
              <div style={{ fontWeight: 700, color: "#1565c0", marginBottom: "8px", fontSize: "0.9rem" }}>
                🗓️ Schedule (optional)
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                <div>
                  <label style={{ fontSize: "0.82rem", color: "#1565c0", display: "block", marginBottom: "3px" }}>Open from</label>
                  <input
                    type="datetime-local"
                    value={quizForm.openFrom}
                    onChange={e => setQuizForm(prev => ({ ...prev, openFrom: e.target.value }))}
                    style={{ padding: "8px", width: "100%", borderRadius: "4px", border: "1px solid #90caf9" }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: "0.82rem", color: "#1565c0", display: "block", marginBottom: "3px" }}>Open until</label>
                  <input
                    type="datetime-local"
                    value={quizForm.openUntil}
                    onChange={e => setQuizForm(prev => ({ ...prev, openUntil: e.target.value }))}
                    style={{ padding: "8px", width: "100%", borderRadius: "4px", border: "1px solid #90caf9" }}
                  />
                </div>
              </div>
              <p style={{ fontSize: "0.78rem", color: "#1976d2", margin: "8px 0 0" }}>
                Leave blank to keep <strong>always open</strong>. Or use the <strong>🚀 Launch</strong> button on the quiz card to open instantly for a set duration.
              </p>
            </div>
          </div>

          {/* LaTeX tip */}
          <div style={{ background: "#fff3e0", border: "1px solid #ffcc80", padding: "10px 14px", borderRadius: "6px", marginBottom: "12px", fontSize: "0.85rem", color: "#e65100" }}>
            💡 <strong>LaTeX supported:</strong> <code>$...$</code> inline · <code>$$...$$</code> block · <code>\(...\)</code> · <code>\[...\]</code>
          </div>

          {/* Questions Section */}
          <div style={{ marginBottom: "15px" }}>
            <h3>Questions ({quizForm.questions.length})</h3>
            {quizForm.questions.map((q, idx) => (
              <div key={idx} style={{ border: "1px solid #ddd", padding: "10px", marginBottom: "10px", borderRadius: "4px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "10px", gap: "10px" }}>
                  <div style={{ flex: 1 }}>
                    <strong style={{ color: "#1565c0" }}>Q{idx + 1}.</strong>{" "}
                    <span style={{ fontSize: "0.92rem" }}><LatexRenderer text={q.question || "(empty)"} /></span>
                  </div>
                  <button
                    onClick={() => handleRemoveQuestion(idx)}
                    style={{ padding: "5px 10px", background: "#f44336", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer", whiteSpace: "nowrap" }}
                  >
                    Remove
                  </button>
                </div>
                {q.type === "mcq" && (
                  <div>
                    <input
                      type="text"
                      value={q.question}
                      onChange={e => handleUpdateQuestion(idx, "question", e.target.value)}
                      placeholder="Question text"
                      style={{ width: "100%", padding: "5px", marginBottom: "5px" }}
                    />
                    {q.options.map((opt, oidx) => (
                      <input
                        key={oidx}
                        type="text"
                        value={opt}
                        onChange={e => {
                          const newOpts = [...q.options];
                          newOpts[oidx] = e.target.value;
                          handleUpdateQuestion(idx, "options", newOpts);
                        }}
                        placeholder={`Option ${oidx + 1}`}
                        style={{ width: "100%", padding: "5px", marginBottom: "5px" }}
                      />
                    ))}
                    <select
                      value={q.answer}
                      onChange={e => handleUpdateQuestion(idx, "answer", parseInt(e.target.value))}
                      style={{ padding: "5px" }}
                    >
                      {q.options.map((_, oidx) => (
                        <option key={oidx} value={oidx}>Answer: Option {oidx + 1}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Add Question Section */}
          <div style={{ background: "#e8f5e9", padding: "15px", borderRadius: "8px", marginBottom: "15px" }}>
            <h4>Add Question</h4>
            <div style={{ marginBottom: "10px" }}>
              <label>Type: </label>
              <select
                value={quizForm.newQuestion.type}
                onChange={e => setQuizForm(prev => ({
                  ...prev,
                  newQuestion: { ...prev.newQuestion, type: e.target.value }
                }))}
                style={{ padding: "5px", marginLeft: "10px" }}
              >
                <option value="mcq">Multiple Choice</option>
                <option value="truefalse">True/False</option>
              </select>
            </div>

            <textarea
              value={quizForm.newQuestion.question}
              onChange={e => setQuizForm(prev => ({
                ...prev,
                newQuestion: { ...prev.newQuestion, question: e.target.value }
              }))}
              placeholder="Question text — supports LaTeX, e.g.  Find the value of $\\int_0^1 x^2 dx$"
              rows={2}
              style={{ width: "100%", padding: "8px", marginBottom: "6px", fontFamily: "inherit" }}
            />
            {quizForm.newQuestion.question?.trim() && (
              <div style={{ background: "#fff", border: "1px solid #c5e1a5", borderRadius: "4px", padding: "8px 12px", marginBottom: "10px" }}>
                <div style={{ fontSize: "0.72rem", color: "#558b2f", fontWeight: 700, marginBottom: "3px" }}>PREVIEW:</div>
                <div style={{ fontSize: "0.92rem" }}><LatexRenderer text={quizForm.newQuestion.question} /></div>
              </div>
            )}

            {quizForm.newQuestion.type === "mcq" && (
              <>
                {quizForm.newQuestion.options.map((opt, idx) => (
                  <input
                    key={idx}
                    type="text"
                    value={opt}
                    onChange={e => {
                      const newOpts = [...quizForm.newQuestion.options];
                      newOpts[idx] = e.target.value;
                      setQuizForm(prev => ({
                        ...prev,
                        newQuestion: { ...prev.newQuestion, options: newOpts }
                      }));
                    }}
                    placeholder={`Option ${idx + 1}`}
                    style={{ width: "100%", padding: "8px", marginBottom: "5px" }}
                  />
                ))}
                <select
                  value={quizForm.newQuestion.answer}
                  onChange={e => setQuizForm(prev => ({
                    ...prev,
                    newQuestion: { ...prev.newQuestion, answer: parseInt(e.target.value) }
                  }))}
                  style={{ padding: "5px" }}
                >
                  {quizForm.newQuestion.options.map((_, idx) => (
                    <option key={idx} value={idx}>Answer: Option {idx + 1}</option>
                  ))}
                </select>
              </>
            )}

            {quizForm.newQuestion.type === "truefalse" && (
              <select
                value={quizForm.newQuestion.answer === 1 ? "true" : "false"}
                onChange={e => setQuizForm(prev => ({
                  ...prev,
                  newQuestion: { ...prev.newQuestion, answer: e.target.value === "true" ? 1 : 0 }
                }))}
                style={{ padding: "5px" }}
              >
                <option value="true">Answer: True</option>
                <option value="false">Answer: False</option>
              </select>
            )}

            <button
              onClick={handleAddQuestion}
              style={{
                marginTop: "10px",
                padding: "8px 16px",
                background: "#4caf50",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Add Question
            </button>
          </div>

          <button
            onClick={handleSaveQuiz}
            disabled={loading}
            style={{
              padding: "10px 20px",
              background: "#1565c0",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "1rem",
              fontWeight: "bold",
            }}
          >
            {loading ? "Saving..." : editingQuiz ? "Update Quiz" : "Create Quiz"}
          </button>
          {editingQuiz && (
            <button
              onClick={resetQuizForm}
              style={{
                marginLeft: "10px",
                padding: "10px 20px",
                background: "#999",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Cancel Edit
            </button>
          )}
        </div>
      )}

      {/* My Quizzes Tab */}
      {activeTab === "myQuizzes" && (
        <div>
          <h2>My Quizzes</h2>
          {myQuizzes.length === 0 ? (
            <p style={{ color: "#999" }}>No quizzes created yet.</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f5f5f5" }}>
                    <th style={{ border: "1px solid #ddd", padding: "10px", textAlign: "left" }}>Title</th>
                    <th style={{ border: "1px solid #ddd", padding: "10px", textAlign: "left" }}>Class</th>
                    <th style={{ border: "1px solid #ddd", padding: "10px", textAlign: "left" }}>Questions</th>
                    <th style={{ border: "1px solid #ddd", padding: "10px", textAlign: "left" }}>Time Limit</th>
                    <th style={{ border: "1px solid #ddd", padding: "10px", textAlign: "left" }}>Status</th>
                    <th style={{ border: "1px solid #ddd", padding: "10px", textAlign: "left" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {myQuizzes.map(quiz => {
                    const status = getQuizStatus(quiz);
                    return (
                    <tr key={quiz.id} style={{ borderBottom: "1px solid #ddd" }}>
                      <td style={{ border: "1px solid #ddd", padding: "10px" }}>{quiz.title}</td>
                      <td style={{ border: "1px solid #ddd", padding: "10px" }}>{quiz.class}</td>
                      <td style={{ border: "1px solid #ddd", padding: "10px" }}>{quiz.questions.length}</td>
                      <td style={{ border: "1px solid #ddd", padding: "10px" }}>{quiz.timeLimit} min</td>
                      <td style={{ border: "1px solid #ddd", padding: "10px" }}>
                        <span style={{ color: status.color, fontWeight: "bold" }}>{status.label}</span>
                        {quiz.openUntil && status.state === "live" && (
                          <div style={{ fontSize: "0.75rem", color: "#666", marginTop: "3px" }}>
                            Until: {new Date(quiz.openUntil).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}
                          </div>
                        )}
                      </td>
                      <td style={{ border: "1px solid #ddd", padding: "10px" }}>
                        {(status.state === "closed" || status.state === "always" || status.state === "upcoming") && (
                          <button
                            onClick={() => { setLaunchingQuiz(quiz); setLaunchDuration(60); }}
                            style={{ padding: "5px 10px", marginRight: "5px", background: "#43a047", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer", fontWeight: "bold" }}
                            title="Launch this quiz to make it live for students"
                          >
                            🚀 Launch
                          </button>
                        )}
                        {status.state === "live" && (
                          <button
                            onClick={() => handleCloseQuiz(quiz)}
                            style={{ padding: "5px 10px", marginRight: "5px", background: "#e53935", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer", fontWeight: "bold" }}
                            title="Close this quiz immediately"
                          >
                            🛑 Close
                          </button>
                        )}
                        <button
                          onClick={() => handleEditQuiz(quiz)}
                          style={{ padding: "5px 10px", marginRight: "5px", background: "#ff9800", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer" }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteQuiz(quiz.id)}
                          style={{ padding: "5px 10px", background: "#f44336", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer" }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Results Tab */}
      {activeTab === "results" && (
        <div>
          <h2>Quiz Results</h2>
          <button
            onClick={handleExportResults}
            disabled={allResults.length === 0 || loading}
            style={{
              marginBottom: "15px",
              padding: "10px 20px",
              background: allResults.length === 0 ? "#ccc" : "#4caf50",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            {loading ? "Exporting..." : `Export to Excel (${allResults.length} results)`}
          </button>

          {allResults.length === 0 ? (
            <p style={{ color: "#999" }}>No results yet. Students will see results here after taking quizzes.</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f5f5f5" }}>
                    <th style={{ border: "1px solid #ddd", padding: "10px", textAlign: "left" }}>Quiz</th>
                    <th style={{ border: "1px solid #ddd", padding: "10px", textAlign: "left" }}>Student</th>
                    <th style={{ border: "1px solid #ddd", padding: "10px", textAlign: "left" }}>Score</th>
                    <th style={{ border: "1px solid #ddd", padding: "10px", textAlign: "left" }}>Percentage</th>
                    <th style={{ border: "1px solid #ddd", padding: "10px", textAlign: "left" }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {allResults.map((result, idx) => (
                    <tr key={idx} style={{ borderBottom: "1px solid #ddd" }}>
                      <td style={{ border: "1px solid #ddd", padding: "10px" }}>{result.quizTitle}</td>
                      <td style={{ border: "1px solid #ddd", padding: "10px" }}>{result.studentName}</td>
                      <td style={{ border: "1px solid #ddd", padding: "10px" }}>{result.score}/{result.total}</td>
                      <td style={{ border: "1px solid #ddd", padding: "10px" }}>{result.percent}%</td>
                      <td style={{ border: "1px solid #ddd", padding: "10px" }}>{new Date(result.submittedAt).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showCropModal && (
        <ImageCropModal
          onClose={() => setShowCropModal(false)}
          onCrop={(croppedUrl) => {
            setQuizForm(prev => ({
              ...prev,
              newQuestion: { ...prev.newQuestion, imageUrl: croppedUrl }
            }));
            setShowCropModal(false);
          }}
        />
      )}

      {/* Launch Quiz Modal */}
      {launchingQuiz && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "#fff", borderRadius: "12px", padding: "30px", maxWidth: "450px", width: "90%", boxShadow: "0 10px 40px rgba(0,0,0,0.3)" }}>
            <h2 style={{ margin: "0 0 15px", color: "#43a047" }}>🚀 Launch Quiz</h2>
            <p style={{ color: "#555", marginBottom: "10px" }}>
              <strong>{launchingQuiz.title}</strong>
            </p>
            <p style={{ color: "#777", marginBottom: "20px", fontSize: "0.9rem" }}>
              Class: {launchingQuiz.class} · Batch: {launchingQuiz.assignedBatch} · {launchingQuiz.questions?.length || 0} questions
            </p>

            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold", color: "#333" }}>
                Duration (minutes): How long should the quiz stay open?
              </label>
              <input
                type="number"
                min="1"
                value={launchDuration}
                onChange={e => setLaunchDuration(e.target.value)}
                style={{ width: "100%", padding: "10px", fontSize: "1rem", border: "1.5px solid #90caf9", borderRadius: "6px" }}
                placeholder="e.g. 60"
              />
              <p style={{ fontSize: "0.85rem", color: "#666", marginTop: "8px" }}>
                Quick options:{" "}
                {[15, 30, 60, 120, 1440].map(m => (
                  <button
                    key={m}
                    onClick={() => setLaunchDuration(m)}
                    style={{ marginLeft: "5px", padding: "3px 10px", background: "#e3f2fd", color: "#1565c0", border: "1px solid #90caf9", borderRadius: "4px", cursor: "pointer", fontSize: "0.8rem" }}
                  >
                    {m >= 60 ? `${m / 60}h` : `${m}m`}
                  </button>
                ))}
              </p>
            </div>

            {launchDuration > 0 && (
              <div style={{ background: "#e8f5e9", padding: "12px", borderRadius: "6px", marginBottom: "20px" }}>
                <p style={{ margin: 0, color: "#2e7d32", fontSize: "0.9rem" }}>
                  ✅ Opens: <strong>Now</strong>
                </p>
                <p style={{ margin: "5px 0 0", color: "#2e7d32", fontSize: "0.9rem" }}>
                  🛑 Closes: <strong>{new Date(Date.now() + launchDuration * 60 * 1000).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}</strong>
                </p>
              </div>
            )}

            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={handleLaunchQuiz}
                disabled={loading}
                style={{ flex: 1, padding: "12px", background: "#43a047", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "bold", fontSize: "1rem" }}
              >
                {loading ? "Launching..." : "🚀 Launch Now"}
              </button>
              <button
                onClick={() => { setLaunchingQuiz(null); setLaunchDuration(60); }}
                style={{ flex: 1, padding: "12px", background: "#ccc", color: "#000", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "bold", fontSize: "1rem" }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeacherDashboard;
